<template>
  <div class="body driver-page">

    <!-- Map -->
    <MapComponent
      ref="mapRef"
      class="main-map"
      :center="mapCenter"
      :zoom="13"
      :markers="markers"
      map-type="neshanVectorNight"
      height="100%"
      width="100%"
      :showSearchBox="false"
    />

    <!-- Loading State -->
    <div v-if="loading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Toggle Buttons -->
    <button class="toggle-btn list-btn" @click="showRequests = !showRequests">
      درخواست‌ها ({{ requests.length }})
    </button>

    <button
      v-if="selectedRequest"
      class="toggle-btn detail-btn"
      @click="showDetails = !showDetails"
    >
      جزئیات
    </button>
    <button
      class="toggle-btn detail-btn"
      @click="goBack"
      :title="$t('back')"
    >
      →
    </button>

    <!-- Requests List -->
    <div
      class="requests-panel"
      :class="{open: showRequests}"
    >
      <div class="panel-header">
        <strong>درخواست‌های ارسال</strong>
        <button class="close-btn" @click="showRequests=false">✕</button>
      </div>

      <div v-if="requests.length === 0" class="empty-state">
        <div class="text-center p-4">
          <div class="mb-2">📦</div>
          <div class="small text-muted">هیچ درخواستی در نزدیکی شما وجود ندارد</div>
        </div>
      </div>

      <div
        v-for="req in requests"
        :key="req.id"
        class="request-card"
        @click="selectRequest(req)"
      >
        <div class="fw-bold">
          {{ req.sender.address }}
        </div>

        <div class="small text-muted">
          → {{ req.receiver.address }}
        </div>

        <div class="small mt-1">
          وسیله: {{ vehicleLabel(req.deliveryType) }}
        </div>
        
        <div class="small text-primary mt-1">
          قیمت: {{ formatPrice(req.estimatedPrice) }}
        </div>
      </div>
    </div>

    <!-- Request Details -->
    <div
      v-if="selectedRequest"
      class="request-details"
      :class="{open: showDetails}"
    >
      <div class="panel-header">
        <strong>جزئیات سفارش</strong>
        <button class="close-btn" @click="showDetails=false">✕</button>
      </div>

      <div class="detail-item">
        <strong>فرستنده</strong>
        <div>{{ selectedRequest.sender.address }}</div>
        <div class="small text-muted">{{ selectedRequest.sender.name || 'نامشخص' }} - {{ selectedRequest.sender.phone || 'نامشخص' }}</div>
      </div>

      <div class="detail-item">
        <strong>گیرنده</strong>
        <div>{{ selectedRequest.receiver.address }}</div>
        <div class="small text-muted">{{ selectedRequest.receiver.name || 'نامشخص' }} - {{ selectedRequest.receiver.phone || 'نامشخص' }}</div>
      </div>

      <div class="detail-item">
        <strong>نوع وسیله</strong>
        <div>{{ vehicleLabel(selectedRequest.deliveryType) }}</div>
      </div>

      <div class="detail-item">
        <strong>پرداخت</strong>
        <div>{{ paymentLabel(selectedRequest.paymentType) }}</div>
      </div>

      <div class="detail-item">
        <strong>توقف</strong>
        <div>{{ selectedRequest.stopMinutes }} دقیقه</div>
      </div>

      <div class="detail-item" v-if="selectedRequest.estimatedPrice">
        <strong>قیمت تخمینی</strong>
        <div class="text-primary fw-bold">{{ formatPrice(selectedRequest.estimatedPrice) }}</div>
      </div>

      <button
        class="btn btn-success w-100 mt-3"
        @click="acceptRequest"
        :disabled="accepting"
      >
        <span v-if="accepting" class="spinner-border spinner-border-sm me-2"></span>
        قبول سفارش
      </button>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue"
import MapComponent from "@/components/MapComponent.vue"
//import nmp from "@neshan-maps-platform/mapbox-gl"
import { deliveryService } from "@/services/api"
import { useRouter } from 'vue-router'

const router = useRouter()
const mapRef = ref(null)

// State
const showRequests = ref(true)
const showDetails = ref(true)
const requests = ref([])
const selectedRequest = ref(null)
const markers = ref([])
const driverInfo = ref(null)
const loading = ref(false)
const accepting = ref(false)

// Map center (Tehran as default)
const mapCenter = computed(() => ({
  lat: driverInfo.value?.lat || 35.6997,
  lng: driverInfo.value?.lng || 51.3380
}))

// Helper functions
function formatPrice(price) {
  if (!price) return 'نامشخص'
  return new Intl.NumberFormat('fa-IR').format(price) + ' تومان'
}

// Load driver info from localStorage or API
onMounted(async () => {
    try {
        loading.value = true
        
        // Try to get driver info from multiple sources
        let user = null
        let driverData = null
        
        try {
            const userStr = localStorage.getItem('user')
            const driverStr = localStorage.getItem('driver')
            
            user = userStr ? JSON.parse(userStr) : {}
            driverData = driverStr ? JSON.parse(driverStr) : {}
        } catch (e) {
            console.error("Error parsing localStorage data:", e)
            user = {}
            driverData = {}
        }
        
        // Get driver ID from various possible locations
        let driverId = null
        
        if (user.driver_id) {
            driverId = user.driver_id
        } else if (driverData.id) {
            driverId = driverData.id
        } else if (user.id) {
            driverId = user.id
        }
        
        if (!driverId) {
            console.log("No driver ID found in localStorage, checking with API...")
            // Try to fetch from API
            try {
                const token = localStorage.getItem('token')
                if (!token) {
                    throw new Error("No authentication token found")
                }
                
                const response = await fetch('/driver/check', {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                })
                
                if (response.ok) {
                    const data = await response.json()
                    if (data.is_driver && data.driver_id) {
                        driverId = data.driver_id
                        // Save for future use
                        localStorage.setItem('driver', JSON.stringify({ id: driverId }))
                    }
                }
            } catch (err) {
                console.error("Failed to fetch driver info:", err)
            }
        }
        
        // Ensure driverId is a number
        if (driverId) {
            driverId = parseInt(driverId)
        }
        
        if (!driverId || isNaN(driverId) || driverId === 0) {
            console.error("Invalid driver ID:", driverId)
            // Redirect to driver registration
            alert("شما به عنوان راننده ثبت نام نکرده اید. لطفاً ابتدا ثبت نام کنید.")
            router.push('/driver/register')
            return
        }
        
        driverInfo.value = {
            id: driverId,
            lat: parseFloat(user.lat) || 35.6997,
            lng: parseFloat(user.lng) || 51.3380
        }
        
        console.log("Driver info loaded:", { id: driverInfo.value.id, lat: driverInfo.value.lat, lng: driverInfo.value.lng })
        
        await loadPendingDeliveries()
        
        // Start polling for new deliveries every 30 seconds
        intervalId = setInterval(loadPendingDeliveries, 30000)
    } catch (error) {
        console.error("Error loading driver info:", error)
        // Don't try to stringify the error
        if (error.message) {
            console.error("Error message:", error.message)
        }
    } finally {
        loading.value = false
    }
})

// Cleanup interval
let intervalId = null
onUnmounted(() => {
    if (intervalId) clearInterval(intervalId)
})

async function loadPendingDeliveries() {
    // Don't try to load if driver ID is invalid
    if (!driverInfo.value || !driverInfo.value.id || isNaN(driverInfo.value.id) || driverInfo.value.id === 0) {
        console.error("Cannot load deliveries: Invalid driver ID", driverInfo.value?.id)
        return
    }
    
    try {
        const response = await deliveryService.getPendingDeliveries(
            driverInfo.value.id,
            driverInfo.value.lat,
            driverInfo.value.lng,
            10 // 10km radius
        )
        
        if (response.data && response.data.deliveries) {
            requests.value = response.data.deliveries.map(delivery => ({
                id: delivery.id,
                sender: {
                    lat: delivery.sender_location.lat,
                    lng: delivery.sender_location.lng,
                    address: delivery.sender_address,
                    name: delivery.sender_name,
                    phone: delivery.sender_phone
                },
                receiver: {
                    lat: delivery.receiver_location.lat,
                    lng: delivery.receiver_location.lng,
                    address: delivery.receiver_address,
                    name: delivery.receiver_name,
                    phone: delivery.receiver_phone
                },
                deliveryType: delivery.delivery_type,
                paymentType: delivery.payment_type,
                stopMinutes: delivery.stop_minutes,
                estimatedPrice: delivery.estimated_price,
                driverName: delivery.driver_name
            }))
            
            console.log(`Loaded ${requests.value.length} deliveries`)
            
            // Refresh markers on map
            //await loadMarkers()
            updateMarkersData()

        }
    } catch (error) {
        console.error("Error loading deliveries:", error)
        if (error.response?.status === 422) {
            console.error("Validation error - check driver_id type:", driverInfo.value.id)
        } else if (error.response?.status === 404) {
            console.log("No deliveries found")
        }
    }
}

function updateMarkersData() {
  markers.value = requests.value.map(req => ({
    lng: req.sender.lng,
    lat: req.sender.lat,
    label: req.sender.address
  }))
}

// async function loadMarkers() {
//     const map = mapRef.value?.getMap()
//     if (!map) {
//         console.log("Map not ready yet")
//         return
//     }
    
//     try {
//         // Clear existing markers
//         markers.value.forEach(marker => {
//             try {
//                 marker.remove()
//             } catch (err) {
//                 console.error("Error removing marker:", err)
//             }
//         })
//         markers.value = []
        
//         requests.value.forEach((req) => {
//             try {
//                 const marker = new nmp.Marker({ color: "red" })
//                     .setLngLat([req.sender.lng, req.sender.lat])
//                     .addTo(map)
                
//                 marker.getElement().addEventListener("click", () => {
//                     selectRequest(req)
//                 })
                
//                 markers.value.push(marker)
//             } catch (err) {
//                 console.error("Error creating marker:", err)
//             }
//         })
        
//         console.log(`Added ${markers.value.length} markers to map`)
//     } catch (error) {
//         console.error("Error in loadMarkers:", error)
//     }
// }

function selectRequest(req) {
    selectedRequest.value = req
    showDetails.value = true
    centerMap(req.sender.lng, req.sender.lat)
    drawRoute(req)
}

function centerMap(lng, lat) {
    const map = mapRef.value?.getMap()
    if (!map) return
    
    try {
        map.flyTo({
            center: [lng, lat],
            zoom: 14,
            speed: 0.8,
            curve: 1.4
        })
    } catch (err) {
        console.error("Error centering map:", err)
    }
}

function drawRoute(req) {
    const map = mapRef.value?.getMap()
    if (!map) return
    
    const routeId = "driver-route"
    
    try {
        if (map.getLayer(routeId)) {
            map.removeLayer(routeId)
            map.removeSource(routeId)
        }
        
        const coords = [
            [req.sender.lng, req.sender.lat],
            [req.receiver.lng, req.receiver.lat]
        ]
        
        map.addSource(routeId, {
            type: "geojson",
            data: {
                type: "Feature",
                geometry: {
                    type: "LineString",
                    coordinates: coords
                }
            }
        })
        
        map.addLayer({
            id: routeId,
            type: "line",
            source: routeId,
            paint: {
                "line-width": 5,
                "line-color": "#4a6cf7"
            }
        })
    } catch (err) {
        console.error("Error drawing route:", err)
    }
}

async function acceptRequest() {
    if (!selectedRequest.value) return
    
    // Validate driver ID
    if (!driverInfo.value || !driverInfo.value.id || isNaN(driverInfo.value.id) || driverInfo.value.id === 0) {
        alert("خطا: اطلاعات راننده یافت نشد. لطفاً دوباره وارد شوید.")
        return
    }
    
    accepting.value = true
    
    try {
        const response = await deliveryService.acceptDelivery(
            selectedRequest.value.id,
            driverInfo.value.id
        )
        
        if (response.data.success) {
            alert("سفارش با موفقیت قبول شد ✅")
            
            // Store the accepted delivery info
            const acceptedDelivery = selectedRequest.value
            
            // Remove accepted delivery from list
            const index = requests.value.findIndex(r => r.id === acceptedDelivery.id)
            if (index !== -1) {
                requests.value.splice(index, 1)
                
                // Remove marker from map
                if (markers.value[index]) {
                    try {
                        markers.value[index].remove()
                    } catch (err) {
                        console.error("Error removing marker:", err)
                    }
                    markers.value.splice(index, 1)
                }
            }
            
            selectedRequest.value = null
            showDetails.value = false
            
            // Redirect to active delivery page
            router.push(`/driver/active-delivery/${acceptedDelivery.id}`)
        }
    } catch (error) {
        console.error("Error accepting delivery:", error)
        if (error.response?.status === 422) {
            alert("خطا در اطلاعات ارسالی. لطفاً دوباره تلاش کنید.")
        } else if (error.response?.status === 400) {
            alert(error.response?.data?.detail || "این سفارش توسط راننده دیگری قبول شده است.")
        } else if (error.response?.status === 404) {
            alert("سفارش یافت نشد.")
        } else {
            alert("خطا در قبول سفارش. لطفاً دوباره تلاش کنید.")
        }
    } finally {
        accepting.value = false
    }
}

function goBack(){
  router.push('/select-role')
}

function vehicleLabel(type) {
    const labels = {
        motor: "موتور",
        bike: "دوچرخه",
        truck: "کامیون"
    }
    return labels[type] || type
}

function paymentLabel(type) {
    const labels = {
        online: "آنلاین",
        sender: "فرستنده",
        receiver: "گیرنده"
    }
    return labels[type] || type
}
</script>

<style scoped>

.driver-page{
  position:relative;
  height:100vh;
}

.map{
  height:100%;
}

/* toggle buttons */

.toggle-btn{
  position:absolute;
  z-index:20;
  background:white;
  border:none;
  padding:8px 12px;
  border-radius:10px;
  box-shadow:0 3px 10px rgba(0,0,0,0.2);
}

.list-btn{
  top:20px;
  left:20px;
}

.detail-btn{
  top:20px;
  right:20px;
}

/* panels */

.requests-panel,
.request-details{
  position:absolute;
  background:white;
  box-shadow:0 4px 20px rgba(0,0,0,0.15);
  border-radius:16px;
  padding:15px;
  z-index:15;
}

/* desktop layout */

.requests-panel{
  top:70px;
  left:20px;
  width:260px;
  max-height:400px;
  overflow:auto;
}

.request-details{
  bottom:20px;
  left:50%;
  transform:translateX(-50%);
  width:340px;
}

/* mobile layout */

@media (max-width:768px){

  .requests-panel{
    width:100%;
    left:0;
    bottom:0;
    top:auto;
    border-radius:20px 20px 0 0;
    max-height:50vh;
    transform:translateY(100%);
    transition:.3s;
  }

  .requests-panel.open{
    transform:translateY(0);
  }

  .request-details{
    width:100%;
    left:0;
    bottom:0;
    transform:translateY(100%);
    border-radius:20px 20px 0 0;
    transition:.3s;
  }

  .request-details.open{
    transform:translateY(0);
  }

}

/* cards */

.request-card{
  background:#f8f9fa;
  border-radius:10px;
  padding:10px;
  margin-bottom:10px;
  cursor:pointer;
}

.request-card:hover{
  background:#eef3ff;
}

.panel-header{
  display:flex;
  justify-content:space-between;
  margin-bottom:10px;
}

.close-btn{
  border:none;
  background:none;
  font-size:18px;
}

.detail-item{
  margin-bottom:10px;
}

</style>
